import React from 'react';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import ArticleIcon from '@mui/icons-material/Article';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Moment from 'moment';
import 'moment/locale/pt-br';
import { useTheme } from '@mui/material/styles';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useSnackbar } from '../hooks/useSnackbar';
import { cryptedParam, isCnpj, serializeQueryString } from '../utils';
import PainelNotaItem from '../components/PainelNotaItem';
import { statuses } from '../utils';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import { useCustomState } from '../hooks/useCustomState';
import { useNavigate } from 'react-router-dom';

let debounce = null;

const PainelNotas = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { user, isUsuarioSebrae } = useAuth();
  const { listarFornecedores, listarNotas } = useQuery();
  const [status, setStatus] = React.useState([]);
  const [notas, setNotas] = React.useState([]);
  const datePickerRef = React.useRef();
  const [loading, setLoading] = React.useState(false);
  const [dataInicio, setDataInicio] = React.useState(Moment().startOf('year'));
  const [dataFim, setDataFim] = React.useState(Moment().endOf('year'));
  const [numNota, setNumNota] = React.useState('');
  const [contrato, setContrato] = React.useState('');
  const [pedido, setPedido] = React.useState('');
  const [queryParams, setQueryParams] = React.useState({
    cnpj: isUsuarioSebrae
      ? ''
      : user.cnpj
      ? user.cnpj.replace(/[^a-zA-Z0-9]/g, '')
      : user.cpf,
    data_inicio: Moment().startOf('year').format('YYYY-MM-DD'),
    data_fim: Moment().endOf('year').format('YYYY-MM-DD'),
    numero_nf: '',
    page: '1',
  });
  const [loadingAutocomplete, setLoadingAutocomplete] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [fornecedor, setFornecedor] = React.useState();
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [lastPage, setLastPage] = React.useState(0);
  const [runRequest, setRunRequest] = React.useState(false);
  const [cnpj, setCnpj] = useCustomState(
    isUsuarioSebrae ? '' : user.cnpj && user.cnpj.replace(/[^a-zA-Z0-9]/g, ''),
  );
  const navigate = useNavigate();

  React.useEffect(() => {
    if (datePickerRef) {
      datePickerRef.current.children[0].classList.remove('Mui-error');
      datePickerRef.current.children[1].classList.remove('Mui-error');
    }
  }, [datePickerRef]);

  React.useEffect(() => {
    if (user && user.role_id == 3) {
      navigate(
        `/dashboard/cadastro-fornecedor?fornecedor_id=${cryptedParam(
          user.fornecedor_id,
        )}`,
        { replace: true },
      );
    }
    listarNotasFornec(cnpj, page);
  }, [runRequest]);

  const listarNotasFornec = async (cnpjFornec, pagina = 1) => {
    try {
      let filters = getFilters();
      filters.cnpj = cnpjFornec;
      filters.paginate = true;
      filters.page = pagina;
      if (!filters.cnpj && filters.cnpj == '') return;
      setLoading(true);
      const { data } = await listarNotas(filters);
      setNotas(data.data.data);
      setPage(data.data.current_page);
      setLastPage(data.data.last_page);
      setTotal(data.data.total);
    } catch (error) {
      snackbar.showSnackbar(error.toString());
    } finally {
      setLoading(false);
    }
  };

  const getFilters = () => {
    var filters = {};

    if (status.length > 0) {
      filters.status = status;
    }
    if (contrato !== '') {
      filters.contrato = contrato;
    }
    if (pedido !== '') {
      filters.pedido = pedido;
    }
    if (dataInicio !== '') {
      filters.data_inicio = dataInicio;
    }
    if (dataFim !== '') {
      filters.data_fim = dataFim;
    }
    if (numNota !== '') {
      filters.num_nota = numNota;
    }

    return filters;
  };

  const fornecedores = async (term) => {
    setLoadingAutocomplete(true);
    const newTerm = isCnpj(term) ? term.replace(/[^a-zA-Z0-9]/g, '') : term;

    const result = await listarFornecedores(newTerm);
    if (result && result.data && result.data.data) {
      const arr = result.data.data.map((item) => ({
        ...item,
        cnpj_sem_pontos: item.cnpj_fornec.replace(/[^a-zA-Z0-9]/g, ''),
      }));
      setOptions(arr);
    }
    setLoadingAutocomplete(false);
  };

  const handleChangeInicio = (value) => {
    setDataInicio(value);
    setQueryParams({ ...queryParams, data_inicio: value.format('YYYY-MM-DD') });
  };

  const handleChangeFim = (value) => {
    setDataFim(value);
    setQueryParams({ ...queryParams, data_fim: value.format('YYYY-MM-DD') });
  };

  const handleOnChangeFiltro = (event) => {
    const finalValue = event.target.value.toUpperCase();
    if (event.target.checked) {
      status.push(finalValue);
      setStatus([...status]);
    } else {
      const idx = status.indexOf(finalValue);
      status.splice(idx, 1);
      setStatus([...status]);
    }
  };

  const onChangeAutoComplete = (event) => {
    if (event.target.value.length > 0) {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        fornecedores(event.target.value);
      }, 1000);
    } else {
      setOptions([]);
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) =>
      option.cnpj_sem_pontos + option.nome_fornec + option.cnpj_fornec,
  });

  const handleChange = (event, value) => {
    setPage(value);
    listarNotasFornec(cnpj, value);
  };

  return (
    <Box
      mt={8}
      sx={{ width: '100%', background: '#FAFCFF', minHeight: '100vh' }}
    >
      <AppBar position="static" color="primary" style={{ padding: 10 }}>
        <Grid container spacing={0}>
          <Grid item display="flex" alignItems="center">
            <ArticleIcon
              sx={{
                fontSize: 40,
              }}
            />
            <span style={{ color: 'white', fontSize: 20 }}>
              Painel de Notas Fiscais
            </span>
          </Grid>
        </Grid>
      </AppBar>

      <Grid container spacing={1} paddingY="2rem" paddingX="1rem">
        {isUsuarioSebrae && (
          <Grid item sm={12} style={{ width: '100%' }}>
            <Box>
              <Autocomplete
                noOptionsText="Nenhuma opção encontrada"
                loadingText="Carregando..."
                getOptionLabel={(option) =>
                  `${option.nome_fornec} - ${option.cnpj_fornec}`
                }
                options={options}
                id="controlled-demo"
                fullWidth
                value={fornecedor}
                loading={loadingAutocomplete}
                filterOptions={filterOptions}
                onChange={(event, newValue) => {
                  setQueryParams({
                    ...queryParams,
                    cnpj: newValue.cnpj_fornec.replace(/[^a-zA-Z0-9]/g, ''),
                  });
                  setCnpj(
                    newValue.cnpj_fornec.replace(/[^a-zA-Z0-9]/g, ''),
                    async () => {
                      setFornecedor(newValue);
                      listarNotasFornec(
                        newValue.cnpj_fornec.replace(/[^a-zA-Z0-9]/g, ''),
                        page,
                      );
                    },
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    onChange={onChangeAutoComplete}
                    {...params}
                    label="Fornecedor Nome ou CNPJ"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingAutocomplete ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        )}

        <Grid
          container
          spacing={1}
          paddingY="2rem"
          direction={{ xs: 'column-reverse', md: 'column-reverse', lg: 'row' }}
        >
          <Grid item sm="12" md="12" lg="9">
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress style={{ marginTop: 60 }} />
              </Box>
            ) : (
              <Grid container spacing={1}>
                {notas &&
                  notas.length > 0 &&
                  notas.map((nota) => {
                    return <PainelNotaItem nota={nota} />;
                  })}
                {notas && notas.length === 0 ? (
                  <p style={{ width: '100%', textAlign: 'center' }}>
                    Nenhum registro encontrado
                  </p>
                ) : null}
                <Grid item sm={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Pagination
                      count={lastPage}
                      page={page}
                      color={'primary'}
                      onChange={handleChange}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid item sm="12" md="12" lg="3">
            <Card
              variant="outlined"
              style={{
                borderTop: `8px solid ${theme.palette.primary.light}`,
              }}
            >
              <CardContent>
                <p
                  style={{
                    color: '#0e72b8',
                    marginTop: 0,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FilterAltIcon color="primary" />
                  Filtro
                </p>
                <p
                  style={{ color: '#0e72b8', marginTop: 0, fontWeight: 'bold' }}
                >
                  Status
                </p>
                <FormGroup>
                  {statuses.map((s, index) => {
                    return (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            onChange={(e) => handleOnChangeFiltro(e)}
                            value={s.toUpperCase()}
                            checked={status.includes(s.toUpperCase())}
                            style={{ padding: '4px' }}
                          />
                        }
                        label={s}
                      />
                    );
                  })}
                </FormGroup>
                <p style={{ color: '#0e72b8', fontWeight: 'bold' }}>
                  Nº da Nota
                </p>
                <TextField
                  style={{ width: '100%', marginBottom: '0.8rem' }}
                  id="outlined-basic"
                  label="Número"
                  value={numNota}
                  onChange={({ target }) => setNumNota(target.value)}
                  variant="outlined"
                  type="number"
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .substring(0, 16)
                      .replace(/[^0-9.]/g, '');
                  }}
                  inputProps={{
                    min: 0,
                    maxLength: 16,
                  }}
                />
                <p style={{ color: '#0e72b8', fontWeight: 'bold' }}>Contrato</p>
                <TextField
                  style={{ width: '100%', marginBottom: '0.8rem' }}
                  id="outlined-basic"
                  label="Número"
                  type="number"
                  value={contrato}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .substring(0, 14)
                      .replace(/[^0-9.]/g, '');
                  }}
                  inputProps={{
                    min: 0,
                    maxLength: 14,
                  }}
                  onChange={({ target }) => setContrato(target.value)}
                  variant="outlined"
                />
                <p style={{ color: '#0e72b8', fontWeight: 'bold' }}>Pedido</p>
                <TextField
                  style={{ width: '100%', marginBottom: '0.8rem' }}
                  id="outlined-basic"
                  label="Número"
                  value={pedido}
                  type="number"
                  onChange={({ target }) => setPedido(target.value)}
                  variant="outlined"
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .substring(0, 16)
                      .replace(/[^0-9.]/g, '');
                  }}
                  inputProps={{
                    min: 0,
                    maxLength: 16,
                  }}
                />
                <p style={{ color: '#0e72b8', fontWeight: 'bold' }}>
                  Data do envio
                </p>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Box>
                    <MobileDatePicker
                      ref={datePickerRef}
                      label="Data início"
                      inputFormat="DD/MM/YYYY"
                      value={dataInicio}
                      onChange={handleChangeInicio}
                      renderInput={(params) => (
                        <TextField
                          error={false}
                          style={{ width: '100%' }}
                          {...params}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ marginTop: 2 }}>
                    <MobileDatePicker
                      ref={datePickerRef}
                      label="Data fim"
                      inputFormat="DD/MM/YYYY"
                      value={dataFim}
                      onChange={handleChangeFim}
                      renderInput={(params) => (
                        <TextField
                          error={false}
                          style={{ width: '100%' }}
                          {...params}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>

                <Button
                  variant="contained"
                  onClick={() => {
                    if (!queryParams.cnpj) {
                      snackbar.showSnackbar(
                        'Selecione um fornecedor antes de buscar.',
                      );
                      return;
                    }

                    listarNotasFornec(cnpj, 1);
                  }}
                  style={{ width: '100%', marginTop: 20 }}
                >
                  Buscar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setDataInicio(
                      Moment().startOf('year').format('YYYY-MM-DD'),
                    );
                    setDataFim(Moment().endOf('year').format('YYYY-MM-DD'));
                    setStatus([]);
                    setNumNota('');
                    setContrato('');
                    setPedido('');

                    setQueryParams({
                      ...queryParams,
                      data_inicio: '',
                      data_fim: '',
                      numero_nf: '',
                      page: '1',
                    });

                    setRunRequest(!runRequest);
                  }}
                  style={{ width: '100%', marginTop: 20 }}
                >
                  Limpar
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PainelNotas;
